if (!customElements.get('smoothie-drawer')) {
  customElements.define(
    'smoothie-drawer',
    class SmoothieDrawer extends HTMLElement {
      constructor() {
        super();
        this.maxSachets = parseInt(this.dataset.maxSachets) || 5;
        this.variantId = this.dataset.variantId || null;
        this.productPrice = parseInt(this.dataset.productPrice) || 0;
        this.prefill = {};
        try {
          this.prefill = JSON.parse(this.dataset.prefill || '{}');
        } catch (e) {
          this.prefill = { mode: 'empty' };
        }

        this.selected = new Map(); // handle → { name, qty }

        this.overlay = this.querySelector('[data-smoothie-overlay]');
        this.panel = this.querySelector('[data-smoothie-panel]');
        this.closeBtn = this.querySelector('[data-smoothie-close]');
        this.submitBtn = this.querySelector('[data-smoothie-submit]');
        this.summaryCount = this.querySelector('[data-summary-count]');
        this.summaryPrice = this.querySelector('[data-summary-price]');
        this.counter = this.querySelector('[data-counter]');
        this.rows = this.querySelectorAll('.smoothie-row');
        this.cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.bindEvents();
        this.applyPrefill();
      }

      connectedCallback() {
        // Drawer disabled — all smoothie products use standard ATC now
      }

      bindEvents() {
        this.overlay.addEventListener('click', () => this.close());
        this.closeBtn.addEventListener('click', () => this.close());
        this.submitBtn.addEventListener('click', () => this.addToCart());

        this.rows.forEach((row) => {
          const minusBtn = row.querySelector('[data-action="minus"]');
          const plusBtn = row.querySelector('[data-action="plus"]');
          plusBtn.addEventListener('click', () => this.changeQty(row, 1));
          minusBtn.addEventListener('click', () => this.changeQty(row, -1));
        });

        this.addEventListener('keyup', (e) => {
          if (e.code === 'Escape') this.close();
        });
      }

      applyPrefill() {
        const mode = this.prefill.mode || 'empty';

        if (mode === 'assorted') {
          // Pre-fill 1 of each flavor
          const flavorRows = Array.from(this.rows);
          const perFlavor = Math.floor(this.maxSachets / flavorRows.length);
          let remaining = this.maxSachets - perFlavor * flavorRows.length;

          flavorRows.forEach((row) => {
            const handle = row.dataset.flavorHandle;
            const qty = perFlavor + (remaining > 0 ? 1 : 0);
            if (remaining > 0) remaining--;

            if (qty > 0) {
              this.selected.set(handle, {
                name: row.dataset.flavorName,
                qty: qty,
              });
              row.querySelector('[data-qty]').textContent = qty;
              row.querySelector('[data-action="minus"]').disabled = false;
              row.classList.add('smoothie-row--active');
            }
          });
        }
        // mode === 'empty' → nothing pre-filled

        this.updateUI();
      }

      open() {
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        requestAnimationFrame(() => {
          this.classList.add('smoothie-drawer--active');
        });
      }

      close() {
        this.classList.remove('smoothie-drawer--active');
        this.panel.addEventListener(
          'transitionend',
          () => {
            this.removeAttribute('open');
            document.body.classList.remove('overflow-hidden');
          },
          { once: true }
        );
      }

      getTotal() {
        let total = 0;
        this.selected.forEach((v) => (total += v.qty));
        return total;
      }

      changeQty(row, delta) {
        const handle = row.dataset.flavorHandle;
        const current = this.selected.has(handle) ? this.selected.get(handle).qty : 0;
        const totalNow = this.getTotal();
        let newQty = current + delta;

        // Clamp
        if (newQty < 0) newQty = 0;
        if (delta > 0 && totalNow >= this.maxSachets) return;

        if (newQty === 0) {
          this.selected.delete(handle);
        } else {
          this.selected.set(handle, {
            name: row.dataset.flavorName,
            qty: newQty,
          });
        }

        // Update row display
        const qtyDisplay = row.querySelector('[data-qty]');
        const minusBtn = row.querySelector('[data-action="minus"]');
        qtyDisplay.textContent = newQty;
        minusBtn.disabled = newQty === 0;
        row.classList.toggle('smoothie-row--active', newQty > 0);

        this.updateUI();
      }

      updateUI() {
        const total = this.getTotal();
        const isComplete = total >= this.maxSachets;
        const atLimit = total >= this.maxSachets;

        // Counter badge
        this.counter.textContent = `${total} / ${this.maxSachets}`;
        this.counter.classList.toggle('smoothie-drawer__step-counter--done', isComplete);

        // Disable all plus buttons when at limit
        this.rows.forEach((row) => {
          const plusBtn = row.querySelector('[data-action="plus"]');
          plusBtn.disabled = atLimit;
        });

        // Summary
        this.summaryCount.textContent = isComplete
          ? `${total} sachets selected`
          : `${total} / ${this.maxSachets} selected`;

        if (this.productPrice > 0) {
          this.summaryPrice.textContent = `₹${(this.productPrice / 100).toLocaleString('en-IN')}`;
        }

        this.submitBtn.disabled = !isComplete;
        this.submitBtn.classList.toggle('smoothie-drawer__cta--ready', isComplete);
      }

      async addToCart() {
        if (this.submitBtn.disabled) return;
        this.submitBtn.setAttribute('aria-disabled', 'true');
        this.submitBtn.classList.add('loading');
        const spinner = this.submitBtn.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        // Build line item properties from selections
        const properties = { _smoothie_bundle: 'true' };
        this.selected.forEach((val, handle) => {
          properties[val.name] = String(val.qty);
        });

        // Also include flavors with 0 for complete visibility
        this.rows.forEach((row) => {
          const name = row.dataset.flavorName;
          if (!properties[name]) {
            properties[name] = '0';
          }
        });

        const sectionIds = this.cart
          ? this.cart.getSectionsToRender().map((s) => s.id)
          : [];

        try {
          const res = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  id: parseInt(this.variantId),
                  quantity: 1,
                  properties: properties,
                },
              ],
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          });

          const data = await res.json();
          if (data.status) {
            console.error('Smoothie cart error:', data.description);
            return;
          }

          this.close();

          if (this.cart && data.sections) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'smoothie-drawer',
              cartData: { sections: data.sections },
            });
            this.cart.renderContents(data);
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (e) {
          console.error('Smoothie add to cart failed:', e);
        } finally {
          this.submitBtn.removeAttribute('aria-disabled');
          this.submitBtn.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      }
    }
  );
}
